import { Schedule } from '../models/Schedule.tsx';
import { scheduleLive } from '../services/player/apis/Schelude.tsx';

const DEFAULT_TTL_MS = 6_000;

export type OddsSnapshotParams = {
  sport?: string;
  league?: string;
  marketId?: string | string[];
  id?: string | null;
  isMain?: boolean;
  startDateAfter?: string;
  startDateBefore?: string;
  timeOffset?: string;
  team_id?: string;
  sportsbooks?: string[];
};

type CacheEntry = {
  value: Schedule[];
  fetchedAt: number;
};

const snapshotCache = new Map<string, CacheEntry>();
const inflight = new Map<string, Promise<Schedule[]>>();

function normalizeMarketId(marketId?: string | string[]): string {
  if (!marketId) return '';
  const parts = (Array.isArray(marketId) ? marketId : [marketId])
    .map(m => String(m).trim())
    .filter(Boolean)
    .sort();
  return parts.join(',');
}

function buildSnapshotKey(params: OddsSnapshotParams): string {
  const id = params.id != null ? String(params.id) : '';
  const markets = normalizeMarketId(params.marketId);
  return [
    id,
    params.sport ?? '',
    params.league ?? '',
    markets,
    params.isMain === true ? '1' : params.isMain === false ? '0' : '',
    params.startDateAfter ?? '',
    params.startDateBefore ?? '',
    params.timeOffset ?? '',
    params.team_id ?? '',
    (params.sportsbooks ?? []).slice().sort().join(','),
  ].join('|');
}

/**
 * Short-lived odds/fixture snapshot cache for bet confirm and ticket building.
 * Deduplicates in-flight requests with the same key and reuses results for a few seconds.
 */
export async function fetchOddsSnapshot(
  params: OddsSnapshotParams,
  options: { ttlMs?: number; force?: boolean } = {}
): Promise<Schedule[]> {
  const ttlMs = options.ttlMs ?? DEFAULT_TTL_MS;
  const key = buildSnapshotKey(params);
  const now = Date.now();

  if (!options.force) {
    const cached = snapshotCache.get(key);
    if (cached && now - cached.fetchedAt < ttlMs) {
      return cached.value;
    }

    const pending = inflight.get(key);
    if (pending) {
      return pending;
    }
  }

  const promise = scheduleLive(params)
    .then((value) => {
      snapshotCache.set(key, { value, fetchedAt: Date.now() });
      return value;
    })
    .finally(() => {
      if (inflight.get(key) === promise) {
        inflight.delete(key);
      }
    });

  if (!options.force) {
    const raced = inflight.get(key);
    if (raced) {
      return raced;
    }
    inflight.set(key, promise);
  } else {
    inflight.set(key, promise);
  }

  return promise;
}

/**
 * Fetch one snapshot per unique fixture id (id-only requests).
 * Useful for pregame started-checks on parlays/teasers/straight batches.
 */
export async function fetchOddsSnapshotsByFixtureIds(
  fixtureIds: Array<string | number | null | undefined>,
  options: { ttlMs?: number; force?: boolean } = {}
): Promise<Map<string, Schedule[]>> {
  const uniqueIds = [...new Set(
    fixtureIds
      .filter((id): id is string | number => id != null && String(id).trim() !== '')
      .map(id => String(id).trim())
  )];

  const entries = await Promise.all(
    uniqueIds.map(async (id) => {
      const games = await fetchOddsSnapshot({ id }, options);
      return [id, games] as const;
    })
  );

  return new Map(entries);
}

export function clearOddsSnapshotCache(): void {
  snapshotCache.clear();
  inflight.clear();
}
